/**
 * Tenant Portal – Privacy Policy (read-only).
 * Linked from tenant login; no signup/accept flow.
 */

import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const Container = styled.div`
  min-height: 100vh;
  background: ${(p) => p.theme.background};
  color: ${(p) => p.theme.foreground};
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
  position: relative;
`;

const VersionBadge = styled.div`
  position: absolute;
  top: 2rem;
  right: 2rem;
  font-size: 0.75rem;
  color: ${(p) => p.theme.mutedForeground};
  @media (max-width: 768px) {
    top: 1rem;
    right: 1rem;
  }
`;

const Header = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${(p) => p.theme.border};
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  color: ${(p) => p.theme.foreground};
`;

const LastUpdated = styled.p`
  font-size: 0.875rem;
  color: ${(p) => p.theme.mutedForeground};
  margin: 0;
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-bottom: 1rem;
  color: ${(p) => p.theme.primary};
  text-decoration: none;
  font-size: 0.875rem;
  &:hover {
    text-decoration: underline;
  }
`;

const Content = styled.div`
  font-size: 1rem;
  color: ${(p) => p.theme.foreground};
  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    margin: 2rem 0 1rem 0;
    color: ${(p) => p.theme.foreground};
  }
  p {
    margin: 1rem 0;
  }
  ul {
    margin: 1rem 0;
    padding-left: 1.5rem;
  }
  li {
    margin: 0.5rem 0;
  }
`;

const FooterLinks = styled.div`
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid ${(p) => p.theme.border};
  font-size: 0.875rem;
  a {
    color: ${(p) => p.theme.primary};
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const TenantPrivacyPolicy: React.FC = () => {
  return (
    <Container>
      <VersionBadge>v1.0</VersionBadge>
      <BackLink to="/tenant/login">← Back to Tenant sign in</BackLink>
      <Header>
        <Title>Privacy Policy – Tenant Portal</Title>
        <LastUpdated>Last updated: 05/01/2026</LastUpdated>
      </Header>
      <Content>
        <p>
          This Privacy Policy explains how TenantPlus ("Circle Plus", "we", "us", "our") collects, uses, stores, and protects your personal information when you use the <strong>Tenant Portal</strong>.
        </p>
        <p>
          By accessing or using the Tenant Portal, you agree to the collection and use of information in accordance with this Privacy Policy.
        </p>
        <h2>1. Information We Collect in the Tenant Portal</h2>
        <p>We may collect:</p>
        <ul>
          <li>Name, email address, phone number</li>
          <li>Login and account details</li>
          <li>Tenancy and payment-related information (e.g. transaction references, amounts)</li>
          <li>Messages or requests you send through the portal</li>
          <li>Device and usage data (e.g. IP address, browser, pages visited) for security and improvement</li>
        </ul>
        <p>
          Some information (e.g. unit, rent) is provided by your landlord; we do not independently verify its accuracy.
        </p>
        <h2>2. How We Use Your Information</h2>
        <p>We use the information to:</p>
        <ul>
          <li>Provide and operate the Tenant Portal</li>
          <li>Enable payments, lease signing, and communication with your landlord</li>
          <li>Send service-related messages (e.g. payment reminders, lease notifications)</li>
          <li>Improve security and platform performance</li>
          <li>Comply with legal obligations</li>
        </ul>
        <p>We do not sell your personal data.</p>
        <h2>3. Sharing of Information</h2>
        <p>
          We may share information with service providers (e.g. hosting, payments, SMS) and where required by law. Your landlord may see information you submit (e.g. maintenance requests, messages) through the platform. We do not share data for advertising or resale.
        </p>
        <h2>4. Data Security and Retention</h2>
        <p>
          We take reasonable measures to protect your data. We retain personal data only as long as necessary to provide services and meet legal requirements.
        </p>
        <h2>5. Your Rights</h2>
        <p>
          Depending on applicable law, you may have the right to access, correct, or request deletion of your personal data. Contact us using the details below to make a request.
        </p>
        <h2>6. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. Continued use of the Tenant Portal after changes constitutes acceptance of the revised policy.
        </p>
        <h2>7. Governing Law and Contact</h2>
        <p>
          This Privacy Policy is governed by the laws of the Republic of Kenya and is issued in accordance with the Kenya Data Protection Act, 2019.
        </p>
        <p>
          <strong>Circle Plus Technologies</strong><br />
          Email: support@tenantplus.co.ke<br />
          Platform: TenantPlus – Tenant Portal
        </p>
      </Content>
      <FooterLinks>
        <Link to="/tenant/terms">Terms of Use (Tenant Portal)</Link>
      </FooterLinks>
    </Container>
  );
};

export default TenantPrivacyPolicy;
